import {produce} from 'immer';
import _ from 'lodash';

import * as ColorUtil from '../../util/colors';
import {expressionToString} from '../../util/expr';
import {getNewGridItemLayout} from '../../util/panelbankGrid';
import {type LayedOutPanel, type LayoutParameters} from '../../util/panelTypes';
import {getLegendOverrideKey} from '../../util/plotHelpers/legend';
import {
  getAllMetrics,
  getMetricIdentifiersFromExpressions,
} from '../../util/plotHelpers/plotHelpers';
import type * as Report from '../../util/report';
import {getFullFlowConfig} from '../../util/sectionFlowConfig';
import {
  getColumnsPerPage,
  getPanelsPerPage,
  getRowsPerPage,
} from '../PanelBank/sectionLayout/resizeUtils';
import {
  type PanelBankFlowSectionConfig,
  type PanelBankSectionConfig,
} from '../PanelBank/types';
import {parseExpressions} from '../PanelExpressionOptions';
import {PANEL_TYPE as runsLinePlotType} from '../PanelRunsLinePlot/types';
import {isPanelGrid} from '../Slate/plugins/panel-grid';
import {
  type SectionSelectionState,
  type SrcSectionOption,
  type UserProjectsQuery,
} from './types';

const overrideKeys = [
  'overrideColors',
  'overrideMarks',
  'overrideSeriesTitles',
];

function addRunNameToOverrides(runName: string, p: LayedOutPanel): void {
  for (const overrideKey of overrideKeys) {
    const override: {[key: string]: unknown} | undefined = (p.config as any)[
      overrideKey
    ];
    if (override != null) {
      for (const k of Object.keys(override)) {
        override[`${runName}:${k}`] = override[k];
        delete override[k];
      }
    }
  }
}

function addDefaultColorsToOverride(runName: string, p: LayedOutPanel): void {
  if (p.viewType !== runsLinePlotType) {
    return;
  }
  const override = p.config.overrideColors ?? {};

  const {expressions, xExpression} = parseExpressions(
    p.config.expressions,
    p.config.xExpression
  );
  const {expressionMetricIdentifiers, xExpressionMetricIdentifiers} =
    getMetricIdentifiersFromExpressions(expressions, xExpression);
  const metrics = getAllMetrics(
    p.config.metrics ?? [],
    expressionMetricIdentifiers,
    xExpressionMetricIdentifiers
  );

  const addToOverride = (metricName: string, i: number) => {
    const key = getLegendOverrideKey({
      uniqueId: runName,
      metricName,
    });
    if (override[key] != null) {
      return;
    }
    const color = ColorUtil.color(i);
    const transparentColor = ColorUtil.color(i, 0.1);
    override[key] = {color, transparentColor};
  };

  for (let i = 0; i < metrics.length; i++) {
    addToOverride(metrics[i], i);
  }
  if (expressions != null && expressions.length > 0) {
    for (let i = 0; i < expressions.length; i++) {
      addToOverride(expressionToString(expressions[i]), i);
    }
  }

  p.config.overrideColors = override;
}

export function normalizePanelLayouts(reportConfig: Report.SlateReport): void {
  reportConfig.blocks.forEach((block, bi) => {
    if (isPanelGrid(block)) {
      const panelLayouts: LayoutParameters[] = [];
      block.metadata.panelBankSectionConfig.panels.forEach((p, pi) => {
        block.metadata.panelBankSectionConfig.panels[pi].layout =
          getNewGridItemLayout(panelLayouts);
        panelLayouts.push(
          block.metadata.panelBankSectionConfig.panels[pi].layout
        );
      });
      reportConfig.blocks[bi] = block;
    }
  });
}

export function normalizeRunPagePanelExport(
  runName: string,
  panels: LayedOutPanel[]
): LayedOutPanel[] {
  return produce(panels, ps => {
    for (const p of ps) {
      addRunNameToOverrides(runName, p);
      addDefaultColorsToOverride(runName, p);
    }
  });
}

export function getTeamsFromUserProjectsQuery(query: UserProjectsQuery) {
  return _.compact(query.data?.user?.teams?.edges.map(e => e.node) ?? []);
}

export function getProjectsFromUserProjectsQuery(query: UserProjectsQuery) {
  const teams = getTeamsFromUserProjectsQuery(query);
  const projectsPerTeam = teams.map(
    t => t?.projects?.edges.map(e => e.node) ?? []
  );
  return _.compact(_.flatten(projectsPerTeam));
}

export function getFirstPagePanels<T>(
  sectionName: string,
  rawFlowConfig: PanelBankSectionConfig['flowConfig'],
  panels: readonly T[]
): T[] {
  const fullFlowConfig = getFullFlowConfig(sectionName, rawFlowConfig);
  const panelsPerPage = getPanelsPerPage({
    columnsPerPage: getColumnsPerPage(fullFlowConfig),
    rowsPerPage: getRowsPerPage(fullFlowConfig),
  });
  return panels.slice(0, panelsPerPage);
}

export const MAX_REPORT_IMPORT_PANELS = 100;

export function getSelectedImportPanelCount(
  sections: readonly SectionSelectionState[]
): number {
  return sections.reduce((sum, section) => {
    if (!section.selected) {
      return sum;
    }
    return (
      sum +
      (section.visibleOnly
        ? section.visiblePanelCount
        : section.totalPanelCount)
    );
  }, 0);
}

/**
 * Keeps panels in section order until `limit` is reached. Later sections are
 * omitted once the limit is hit. If the limit falls inside a section, that
 * section keeps only its leading panels.
 */
export function takeFirstPanelsBySection(
  panelsBySection: readonly LayedOutPanel[][],
  limit: number = MAX_REPORT_IMPORT_PANELS
): LayedOutPanel[][] {
  let remaining = limit;
  const result: LayedOutPanel[][] = [];
  for (const panels of panelsBySection) {
    if (remaining <= 0) {
      break;
    }
    const taken = panels.slice(0, remaining);
    result.push(taken);
    remaining -= taken.length;
  }
  return result;
}
